import React from 'react'
import { Modal, Input, Button, Label, MessageEditor } from '@/shared/ui'

export type SuggestionFormValues = {
  title: string
  content: string
  thresholdPct?: number
}

type Props = {
  open: boolean
  mode: 'create' | 'edit'
  initial?: SuggestionFormValues
  saving?: boolean
  enableThreshold?: boolean
  onClose: () => void
  onSubmit: (values: SuggestionFormValues) => Promise<void> | void
}

export default function SuggestionFormModal({
  open,
  mode,
  initial,
  saving,
  enableThreshold,
  onClose,
  onSubmit,
}: Props) {
  const [title, setTitle] = React.useState(initial?.title ?? '')
  const [content, setContent] = React.useState(initial?.content ?? '')
  const [threshold, setThreshold] = React.useState<number>(initial?.thresholdPct ?? 51)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (open) {
      setTitle(initial?.title ?? '')
      setContent(initial?.content ?? '')
      setThreshold(initial?.thresholdPct ?? 51)
      setError(null)
    }
  }, [open, initial?.title, initial?.content, initial?.thresholdPct])

  async function handleSubmit() {
    const t = title.trim()
    const c = content.trim()
    if (!t || !c) {
      setError('Preencha título e descrição da sugestão.')
      return
    }
    setError(null)
    const payload: SuggestionFormValues = { title: t, content: c }
    if (enableThreshold) {
      const v = Number(threshold)
      if (Number.isFinite(v)) {
        const clamped = Math.min(100, Math.max(1, Math.round(v)))
        payload.thresholdPct = clamped
      }
    }
    await onSubmit(payload)
  }

  const titleText = mode === 'create' ? 'Nova sugestão' : 'Editar sugestão'

  return (
    <Modal open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <div className="space-y-4">
        <div className="text-lg font-semibold">{titleText}</div>
        <div className="grid gap-2">
          <Label>Título</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex.: Novo relatório de desempenho dos sinais"
          />
        </div>
        <div className="grid gap-2">
          <Label>Descrição</Label>
          <MessageEditor value={content} onChange={setContent} height={320} />
        </div>
        {enableThreshold && (
          <div className="grid gap-2">
            <Label>Alvo de aprovação (%)</Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={threshold}
              onChange={(e) => {
                const v = Number(e.target.value)
                if (!Number.isFinite(v)) {
                  setThreshold(51)
                } else {
                  const clamped = Math.min(100, Math.max(1, Math.round(v)))
                  setThreshold(clamped)
                }
              }}
            />
            <div className="text-xs text-foreground/60">
              Percentual mínimo de usuários elegíveis que devem votar a favor
              para considerar esta sugestão aprovada pela comunidade.
            </div>
          </div>
        )}
        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            type="button"
            onClick={onClose}
            disabled={!!saving}
          >
            Cancelar
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!!saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

